import { useState } from 'react'
import { Button } from '../../atoms/Button'
import { Input } from '../../atoms/Input'
import { Select } from '../../atoms/Select'
import { Spinner } from '../../atoms/Spinner'
import { SheetSection } from '../SheetSection'
import { errorMessage } from '../../../lib/api'
import { changedFields, emptyRequiredFields, GENDER_LABELS, type FormField, type ProfileDraft } from '../../../lib/profile'
import type { ProfilePatch, ProfileRequiredField } from '../../../types'

interface FieldSpec {
	label: string
	hint?: string
	type?: 'text' | 'tel' | 'date' | 'gender'
	autoComplete?: string
}

const FIELD_SPECS: Record<FormField, FieldSpec> = {
	name: { label: 'Display name', hint: 'What the team calls you, shown everywhere in the app', autoComplete: 'nickname' },
	phone: { label: 'Phone (Zalo)', type: 'tel', autoComplete: 'tel' },
	legal_name: { label: 'Full legal name', hint: 'Exactly as on your ID card or passport', autoComplete: 'name' },
	gender: { label: 'Gender', type: 'gender', autoComplete: 'sex' },
	date_of_birth: { label: 'Date of birth', type: 'date', autoComplete: 'bday' },
	address: { label: 'Address of residence', autoComplete: 'street-address' },
	id_number: { label: 'ID card (CCCD) or passport number' },
	id_issued_on: { label: 'ID issued on', type: 'date' },
	id_issued_by: { label: 'ID issued by', hint: 'As printed on the card' },
	emergency_name: { label: 'Name' },
	emergency_relation: { label: 'Relationship', hint: 'For example mother, partner, friend' },
	emergency_phone: { label: 'Phone', type: 'tel' },
	start_date: { label: 'First working day', type: 'date' },
}

const SECTIONS: { title: string; hint?: string; fields: FormField[] }[] = [
	{ title: 'You', fields: ['name', 'phone'] },
	{
		title: 'For your contract',
		hint: 'Only you and managers can see this.',
		fields: ['legal_name', 'gender', 'date_of_birth', 'address', 'id_number', 'id_issued_on', 'id_issued_by'],
	},
	{
		title: 'Emergency contact',
		hint: 'Who we call if something happens during a shift.',
		fields: ['emergency_name', 'emergency_relation', 'emergency_phone'],
	},
	{ title: 'Employment', fields: ['start_date'] },
]

interface ProfileFormProps {
	initial: ProfileDraft
	shown: readonly FormField[]
	editable: readonly FormField[]
	required: readonly ProfileRequiredField[]
	lockedHint?: string
	submitLabel: string
	onSubmit: (patch: ProfilePatch) => Promise<void>
}

function fieldOfError(message: string, shown: readonly FormField[]): FormField | null {
	const prefix = message.split(':')[0]
	return (shown as readonly string[]).includes(prefix) ? (prefix as FormField) : null
}

function displayValue(field: FormField, value: string): string {
	if (!value) return 'Not set'
	return field === 'gender' ? (GENDER_LABELS[value as keyof typeof GENDER_LABELS] ?? value) : value
}

export function ProfileForm({ initial, shown, editable, required, lockedHint, submitLabel, onSubmit }: ProfileFormProps) {
	const [draft, setDraft] = useState<ProfileDraft>(initial)
	const [errors, setErrors] = useState<Partial<Record<FormField, string>>>({})
	const [formError, setFormError] = useState<string | null>(null)
	const [isSaving, setIsSaving] = useState(false)

	const patch = changedFields(initial, draft, editable)
	const hasChanges = Object.keys(patch).length > 0
	const hasLockedValues = shown.some((field) => !editable.includes(field) && draft[field] !== '')

	function update(field: FormField, value: string) {
		setDraft((previous) => ({ ...previous, [field]: value }))
		setErrors((previous) => ({ ...previous, [field]: undefined }))
		setFormError(null)
	}

	async function submit(event: React.FormEvent) {
		event.preventDefault()
		const empty = emptyRequiredFields(draft, required).filter((field) => editable.includes(field))
		if (empty.length > 0) {
			setErrors(Object.fromEntries(empty.map((field) => [field, 'Required'])))
			setFormError('Fill in the marked fields.')
			return
		}
		setIsSaving(true)
		try {
			await onSubmit(patch)
		} catch (error) {
			const message = errorMessage(error, 'Saving failed. Try again.')
			const field = fieldOfError(message, shown)
			if (field) setErrors({ [field]: message.slice(field.length + 1).trim() })
			setFormError(message)
		} finally {
			setIsSaving(false)
		}
	}

	function renderField(field: FormField) {
		const spec = FIELD_SPECS[field]
		const value = draft[field]

		if (!editable.includes(field)) {
			return (
				<div key={field} className="flex flex-col gap-0.5">
					<span className="text-xs text-muted-foreground">{spec.label}</span>
					<span className="flex items-center gap-1.5 text-sm text-foreground">
						{displayValue(field, value)}
						{lockedHint && value && <i className="ti ti-lock text-xs text-muted-foreground" aria-hidden="true" />}
					</span>
				</div>
			)
		}

		const inputId = `profile-${field}`
		return (
			<div key={field} className="flex flex-col gap-1">
				<label htmlFor={inputId} className="text-xs text-muted-foreground">
					{spec.label}
				</label>
				{spec.type === 'gender' ? (
					<Select id={inputId} value={value} error={errors[field]} onChange={(event) => update(field, event.target.value)}>
						<option value="">Choose</option>
						{Object.entries(GENDER_LABELS).map(([gender, genderLabel]) => (
							<option key={gender} value={gender}>
								{genderLabel}
							</option>
						))}
					</Select>
				) : (
					<Input
						id={inputId}
						type={spec.type ?? 'text'}
						inputMode={spec.type === 'tel' ? 'tel' : undefined}
						autoComplete={spec.autoComplete ?? 'off'}
						value={value}
						error={errors[field]}
						onChange={(event) => update(field, event.target.value)}
					/>
				)}
				{spec.hint && !errors[field] && <span className="text-xs text-muted-foreground">{spec.hint}</span>}
			</div>
		)
	}

	return (
		<form onSubmit={submit} className="flex flex-col gap-4" noValidate>
			{SECTIONS.map((section) => {
				const fields = section.fields.filter((field) => shown.includes(field))
				if (fields.length === 0) return null
				return (
					<SheetSection key={section.title} title={section.title} hint={section.hint}>
						<div className="flex flex-col gap-3">{fields.map(renderField)}</div>
					</SheetSection>
				)
			})}
			{lockedHint && hasLockedValues && (
				<p className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<i className="ti ti-lock" aria-hidden="true" />
					{lockedHint}
				</p>
			)}
			{formError && <p className="text-sm text-error">{formError}</p>}
			<Button type="submit" variant="primary" fullWidth disabled={isSaving || !hasChanges}>
				{isSaving ? <Spinner className="size-4" /> : submitLabel}
			</Button>
		</form>
	)
}
